import { Collapse, Dialog, LinearProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import config from '../../config.json'
import { post } from '../helpers/Api'
import { sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import { useSharedState } from '../waxplorer/Waxplorer'
import { DropAlert } from './DropAlert'
import { DropPaper } from './DropPaper'
import { useTranslation } from 'react-i18next'
import { Input } from '@nextui-org/react'
import { inputStyles } from '../common/styles'
import MainButton from '../common/util/input/MainButton'

export const ManageDropRamDialog = ({
    open,
    setOpen,
    title,
    collectionName,
    ramBalance,
    getRamBalance,
    ramContract,
}) => {
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const { t } = useTranslation('common')

    const [isManagingBalance, setIsManagingBalance] = useState(false)
    const [isLoadingRamRate, setIsLoadingRamRate] = useState(true)
    const [ramToBuyInWax, setRamToBuyInWax] = useState(0)
    const [ramRate, setRamRate] = useState({ base: 1, quote: 1 })
    const [ramBalanceInBytesPreview, setRamBalanceInBytesPreview] = useState(0)

    const [error, setError] = useState(null)

    const getRamRate = async () => {
        const body = {
            code: 'eosio',
            index_position: 1,
            json: 'true',
            key_type: 'i64',
            limit: 1,
            lower_bound: '',
            upper_bound: '',
            reverse: 'true',
            scope: 'eosio',
            show_payer: 'false',
            table: 'rammarket',
            table_key: '',
        }

        const url =
            process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                ? config.testapi + '/v1/chain/get_table_rows'
                : state.api + '/v1/chain/get_table_rows'
        const res = await post(url, body)

        if (res && res.status === 200 && res.data && res.data.rows) {
            const market = res.data.rows[0]
            const base = market['base']
            const quote = market['quote']

            setRamRate({
                base: parseFloat(base['balance'].replace(' RAM', '')),
                quote: parseFloat(quote['balance'].replace(' WAX', '')),
            })

            setRamBalanceInBytesPreview(
                ramBalance +
                    (ramToBuyInWax > 0
                        ? parseInt(
                              (parseFloat(ramToBuyInWax) *
                                  (ramRate.base / ramRate.quote)) /
                                  1.01,
                          )
                        : 0),
            )
        }

        setIsLoadingRamRate(false)
    }

    const handleWaxRamUpdate = (waxRamInput) => {
        setRamToBuyInWax(waxRamInput)

        setRamBalanceInBytesPreview(
            ramBalance +
                (waxRamInput > 0
                    ? parseInt(
                          (waxRamInput * (ramRate.base / ramRate.quote)) / 1.01,
                      )
                    : 0),
        )
    }

    const withdrawAll = async () => {
        setIsManagingBalance(true)
        setError(null)

        const withdrawRamActions = [getBoostAction(activeUser)]

        withdrawRamActions.push({
            account: ramContract,
            name: 'withdrawram',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                authorized_account: userName,
                collection_name: collectionName,
                recipient: userName,
                bytes: ramBalance,
            },
        })

        let withdrawRamError = null
        let withdrawRamTransactionId = null

        try {
            const withdrawRamResult = await activeUser.session.transact(
                {
                    actions: withdrawRamActions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            withdrawRamTransactionId =
                withdrawRamResult?.response?.transaction_id
        } catch (e) {
            withdrawRamError = e.message
            setError(e.message)
        }
        handleWaxRamUpdate(0)
        await sleep(4000)
        getRamBalance()
        setIsManagingBalance(false)
    }

    const buyRam = async () => {
        setIsManagingBalance(true)
        setError(null)

        const buyRamActions = [getBoostAction(activeUser)]

        if (ramToBuyInWax && parseFloat(ramToBuyInWax) > 0) {
            buyRamActions.push({
                account: 'eosio.token',
                name: 'transfer',
                authorization: [
                    {
                        actor: userName,
                        permission: activeUser.requestPermission
                            ? activeUser.requestPermission
                            : 'active',
                    },
                ],
                data: {
                    from: userName,
                    to: ramContract,
                    quantity: `${parseFloat(ramToBuyInWax).toFixed(8)} WAX`,
                    memo: 'deposit_collection_ram:' + collectionName,
                },
            })
        }

        let buyRamError = null
        let buyRamTransactionId = null

        try {
            const buyRamResult = await activeUser.session.transact(
                {
                    actions: buyRamActions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            buyRamTransactionId = buyRamResult?.response?.transaction_id
        } catch (e) {
            buyRamError = e.message
            setError(e.message)
        }
        handleWaxRamUpdate(0)
        await sleep(4000)
        getRamBalance()
        setIsManagingBalance(false)
    }

    useEffect(() => {
        getRamRate()
    }, [])

    const close = () => {
        if (!isLoadingRamRate && !isManagingBalance) {
            setOpen(false)
            setRamToBuyInWax(0)
            setRamBalanceInBytesPreview(0)
        }
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="xs" fullWidth>
            <DropPaper noMargin title={title}>
                <div>
                    <div>
                        {isLoadingRamRate ? (
                            <LinearProgress />
                        ) : (
                            <div className="text-center">
                                {isManagingBalance ? (
                                    <div className="my-5">
                                        <LinearProgress />
                                    </div>
                                ) : (
                                    <div>
                                        <div>Current RAM Balance:</div>
                                        <div>{ramBalance} Bytes</div>
                                    </div>
                                )}

                                <Collapse in={ramBalance > 0}>
                                    <div>
                                        <div className="my-5">
                                            <MainButton
                                                fullWidth={false}
                                                isDisabled={
                                                    isLoadingRamRate ||
                                                    isManagingBalance ||
                                                    ramBalance === 0
                                                }
                                                onClick={() => withdrawAll()}
                                            >
                                                Withdraw all
                                            </MainButton>
                                        </div>
                                        <div className="italic font-bold">
                                            - or buy more-
                                        </div>
                                    </div>
                                </Collapse>
                                <div className="my-5">
                                    <Input
                                        label="RAM to Buy in WAX"
                                        size="md"
                                        classNames={{
                                            ...inputStyles,
                                        }}
                                        value={ramToBuyInWax}
                                        required={true}
                                        type="number"
                                        placeholder="0"
                                        onWheel={(e) => e.target.blur()}
                                        InputProps={{ inputProps: { min: 0 } }}
                                        isInvalid={
                                            ramToBuyInWax <= 0 ? true : false
                                        }
                                        onChange={(e) => {
                                            handleWaxRamUpdate(e.target.value)
                                        }}
                                        helperText={
                                            ramToBuyInWax <= 0
                                                ? 'Amount must be >0'
                                                : ''
                                        }
                                        isDisabled={
                                            isLoadingRamRate ||
                                            isManagingBalance
                                        }
                                    />
                                </div>
                                <Collapse in={parseFloat(ramToBuyInWax) > 0}>
                                    {ramContract === 'nfthivedrops' ? (
                                        <div className="mb-5">
                                            Buying RAM for{' '}
                                            <b>{ramToBuyInWax} WAX</b> will
                                            bring your balance to ≈
                                            <b>
                                                {ramBalanceInBytesPreview} Bytes
                                            </b>
                                            , which allows for approx.
                                            <br />
                                            <b>
                                                {Intl.NumberFormat().format(
                                                    parseInt(
                                                        Math.floor(
                                                            ramBalanceInBytesPreview /
                                                                151,
                                                        ),
                                                    ),
                                                )}
                                            </b>{' '}
                                            Standard Drop Mints
                                            <br />
                                            <b>
                                                {Intl.NumberFormat().format(
                                                    parseInt(
                                                        Math.floor(
                                                            ramBalanceInBytesPreview /
                                                                450,
                                                        ),
                                                    ),
                                                )}
                                            </b>{' '}
                                            PFP Drop Mints
                                        </div>
                                    ) : (
                                        <div>
                                            {t('packs.buying_ram_packs_info', {
                                                x: ramToBuyInWax,
                                                y: ramBalanceInBytesPreview,
                                                z: Intl.NumberFormat().format(
                                                    parseInt(
                                                        Math.floor(
                                                            ramBalanceInBytesPreview /
                                                                151,
                                                        ),
                                                    ),
                                                ),
                                            })}
                                        </div>
                                    )}
                                </Collapse>
                            </div>
                        )}
                    </div>
                </div>
                <DropAlert
                    condition={error !== null}
                    severity="error"
                    title="An error occured!"
                >
                    {error}
                </DropAlert>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                    <MainButton
                        fullWidth={false}
                        onClick={() => buyRam()}
                        isDisabled={
                            ramToBuyInWax <= 0 ||
                            isManagingBalance ||
                            isLoadingRamRate
                        }
                    >
                        Buy RAM
                    </MainButton>

                    <MainButton color="danger" onClick={() => close()}>
                        Cancel
                    </MainButton>
                </div>
            </DropPaper>
        </Dialog>
    )
}
